import { useItineraryContext } from '../../services/context/itineraryContext';
import Modal, { ModalFooter, ModalHeader, ModalSection } from '@kiwicom/orbit-components/lib/Modal';
import Itinerary from '../../Itinerary';
import { Category } from '@/common/services/logging';
import React from 'react';
import Text from '@kiwicom/orbit-components/lib/Text';
import Stack from '@kiwicom/orbit-components/lib/Stack';
import AirplaneUpOff from '@kiwicom/orbit-components/lib/icons/AirplaneUpOff';
import { FormattedMessage } from 'react-intl';
import Button from '@kiwicom/orbit-components/lib/Button';
import useMediaQuery from '@kiwicom/orbit-components/lib/hooks/useMediaQuery';
import Box from '@kiwicom/orbit-components/lib/Box';
import Portal from '@kiwicom/orbit-components/lib/Portal';
import useLabelClose from '../../../../services/hooks/useLabelClose';

type ThrowAwayTicketModalProps = {
  onClose: () => void;
};

/**
 * Modal with information about a TAT segment. It contains a list of badges informing user about how the TAT works and its risks.
 * Additionally, it contains an Itinerary component showing the unused flights of the whole Itinerary.
 *
 * @param onClose - function called when the modal is closed
 *
 * @returns ThrowAwayTicketModal component
 */
const ThrowAwayTicketModal = ({ onClose }: ThrowAwayTicketModalProps) => {
  const { throwAwaySectors, tatModalDescription } = useItineraryContext();
  const { isLargeMobile } = useMediaQuery();
  const labelClose = useLabelClose();

  if (!throwAwaySectors) {
    return null;
  }

  return (
    // The portal prevents outer CSS from applying.
    <Portal>
      {/* We don't want clicks on the modal to register as clicks on the itinerary segment card. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div onClick={e => e.stopPropagation()}>
        <Modal onClose={onClose} labelClose={labelClose}>
          <ModalHeader
            title={
              <FormattedMessage
                id="mmb.itinerary.throwaway_modal.title"
                defaultMessage="Throwaway ticket"
              />
            }
            description={tatModalDescription}
            suppressed
          />
          <ModalSection>
            <Stack>
              <Stack direction="row" spacing="200">
                <AirplaneUpOff size="large" ariaHidden />
                <Text size="large">
                  <FormattedMessage
                    id="mmb.itinerary.throwaway_modal.section.title"
                    defaultMessage="Extra return flight"
                  />
                </Text>
              </Stack>
              <Itinerary
                itinerary={{ sectors: throwAwaySectors }}
                trackingProps={{
                  category: Category.COMMON,
                  props: {
                    modal: 'throw-away-ticket',
                  },
                }}
                tatModalDescription={null}
              />
            </Stack>
          </ModalSection>
          <ModalFooter flex="1 1 100%">
            <Box display="flex" justify="end">
              <Button onClick={onClose} fullWidth={!isLargeMobile}>
                <FormattedMessage
                  id="mmb.itinerary.throwaway_modal.button"
                  defaultMessage="I understand"
                />
              </Button>
            </Box>
          </ModalFooter>
        </Modal>
      </div>
    </Portal>
  );
};

export default ThrowAwayTicketModal;
